import React, { useEffect } from 'react';
import { Card, Form, Input, Button, Typography, message } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import authService from '../services/auth';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  useDocumentTitle('重置密码');
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    if (email) {
      form.setFieldsValue({ email });
    }
    if (!token) {
      message.warning('缺少重置令牌，请重新请求重置邮件');
    }
  }, [searchParams, form]);

  const handleFinish = async (values: any) => {
    const payload = {
      email: values.email,
      token: searchParams.get('token') || '',
      password: values.password,
      password_confirmation: values.password_confirmation,
    };

    try {
      const res = await authService.resetPassword(payload);
      if (res.success) {
        message.success(res.message || '密码重置成功，请重新登录');
        navigate('/login');
      } else {
        message.error(res.message || '重置失败，请稍后重试');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '重置失败，请稍后再试');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#eef2f6',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <Card style={{ boxShadow: '0 12px 30px rgba(27, 74, 163, 0.15)', borderRadius: '12px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={3} style={{ marginBottom: '8px' }}>重置密码</Title>
            <Text type="secondary">设置一个新的登录密码</Text>
          </div>

          <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off" size="large">
            <Form.Item
              label={<span style={{ fontWeight: 600 }}>邮箱</span>}
              name="email"
              rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '邮箱格式不正确' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="请输入注册邮箱"
                style={{ height: '48px', borderRadius: '10px' }}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 600 }}>新密码</span>}
              name="password"
              rules={[{ required: true, message: '请输入新密码' }, { min: 8, message: '至少 8 位字符' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="请输入新密码"
                style={{ height: '48px', borderRadius: '10px' }}
              />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontWeight: 600 }}>确认新密码</span>}
              name="password_confirmation"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="请再次输入新密码"
                style={{ height: '48px', borderRadius: '10px' }}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              style={{ height: '48px', borderRadius: '10px', fontWeight: 700, letterSpacing: '1px' }}
            >
              确认重置
            </Button>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
